import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { blogAPI } from '../services/api';
import { Clock, User, Loader2 } from 'lucide-react';
import { Badge } from '../components/ui/badge';

const BlogPage = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState(['All']);

  useEffect(() => {
    let isMounted = true;

    const fetchPosts = async () => {
      try {
        const response = await blogAPI.getAll();
        if (isMounted) {
          const posts = response.data || [];
          setBlogPosts(posts);
          
          // Extract unique categories
          const uniqueCategories = ['All', ...new Set(posts.map(p => p.category).filter(Boolean))];
          setCategories(uniqueCategories);
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Error fetching blog posts:', error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchPosts();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredPosts = selectedCategory === 'All' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-green" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-warm-white">
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-charcoal mb-4 font-heading">
            Wellness Journal
          </h1>
          <p className="text-xl text-stone-grey font-body max-w-2xl mx-auto">
            Expert insights for mindful living, better sleep, and stress-free days
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-5 py-2 rounded-full font-medium transition-colors font-body ${
                  selectedCategory === category
                    ? 'bg-muted-green text-charcoal'
                    : 'bg-white border border-soft-sand text-charcoal hover:border-muted-green'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        )}

        {/* Blog Posts Grid */}
        {filteredPosts.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-stone-grey font-body">No blog posts available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Link 
                key={post._id || post.id} 
                to={`/blog/${post.slug}`}
                className="group japandi-card overflow-hidden hover-lift"
              >
                <div className="aspect-[16/10] overflow-hidden bg-soft-sand relative">
                  <img
                    src={post.image || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=600'}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {post.category && (
                    <div className="absolute top-4 left-4">
                      <Badge className="bg-muted-green text-white font-body">
                        {post.category}
                      </Badge>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-charcoal mb-3 group-hover:text-muted-green transition-colors line-clamp-2 font-heading">
                    {post.title}
                  </h3>
                  <p className="text-stone-grey mb-4 line-clamp-2 font-body">
                    {post.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-sm text-stone-grey font-body">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      <span>{post.author?.name || 'BOXVERRA'}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      <span>{post.read_time || post.readTime || '5 min read'}</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
